import { Flex, Link as ChakraLink, useBoolean } from "@chakra-ui/react";
import { FC } from "react";

import { Extension } from "api/get-extensions";
import { Integration } from "api/get-integrations";
import { User } from "api/get-user";
import { Nav } from "components/nav";
import { Topbar } from "components/topbar";
import { useScrollFade } from "lib/use-scroll-fade";

type Props = {
  loadingUser?: boolean;
  user?: User;
  destinations: Integration[];
  sources: Integration[];
  extensions: Extension[];
};

export const Layout: FC<Readonly<Props>> = ({
  children,
  loadingUser,
  ...props
}) => {
  const [mobileNavOpen, { toggle: toggleMobileNav }] = useBoolean(false);
  const navFade = useScrollFade();

  if (!props.destinations || !props.sources) {
    return (
      <Flex
        pt={24}
        pb={16}
        direction="column"
        flex={1}
        width="100%"
        maxWidth="1280px"
        mx="auto"
      >
        {children}
      </Flex>
    );
  }

  return (
    <>
      <ChakraLink
        href="#main-content"
        position="fixed"
        top={2}
        left={2}
        zIndex={1500}
        px={4}
        py={2}
        bg="white"
        color="link.default"
        fontWeight="medium"
        borderRadius="base"
        boxShadow="sm"
        transform="translateY(-200%)"
        transition="transform 150ms ease-in-out"
        _focusVisible={{
          transform: "translateY(0)",
          boxShadow: "outline",
          outline: "none",
        }}
      >
        Skip to content
      </ChakraLink>
      <Topbar
        {...props}
        toggleNav={toggleMobileNav}
        navOpen={mobileNavOpen}
        loadingUser={loadingUser}
      />
      {mobileNavOpen && (
        <Flex
          id="mobile-nav"
          display={["flex", "flex", "none"]}
          bg="white"
          direction="column"
          pos="sticky"
          top="var(--theme-sizes-header)"
          left={0}
          height="calc(100vh - var(--theme-sizes-header))"
          width="100%"
          pb={6}
          overflow="auto"
        >
          <Nav {...props} label="Documentation (mobile)" toggle={toggleMobileNav} />
        </Flex>
      )}
      <Flex width="100%">
        <Flex
          ref={navFade.ref}
          display={["none", "none", "flex"]}
          id="nav"
          bg="white"
          direction="column"
          pos="sticky"
          top="var(--theme-sizes-header)"
          left={0}
          height="calc(100vh - var(--theme-sizes-header))"
          width="300px"
          flexShrink={0}
          overflow="auto"
          overscrollBehavior="contain"
          borderRight="1px"
          borderColor="base.divider"
          sx={{
            maskImage: navFade.maskImage,
            WebkitMaskImage: navFade.maskImage,
          }}
        >
          <Nav {...props} label="Documentation" />
        </Flex>
        <Flex
          direction="column"
          pt={6}
          pb={8}
          flex="1"
          minW={0}
          display={mobileNavOpen ? "none" : "flex"}
        >
          {children}
        </Flex>
      </Flex>
    </>
  );
};
